export interface CachedPage<T> {
  data: T[];
  totalCount?: number;
  hasMore?: boolean;
}

export interface CacheLookup<T> {
  page: CachedPage<T>;
  /** True once the entry is older than `staleTime` (serve-then-revalidate). */
  isStale: boolean;
}

interface CacheEntry<T> {
  page: CachedPage<T>;
  storedAt: number;
}

export interface PaginationCacheOptions {
  /**
   * How long a cached page is considered fresh, in ms. While fresh it is served
   * without a background refetch. Default 30s.
   */
  staleTime?: number;
  /**
   * How long a page stays in the cache before it is dropped entirely, in ms.
   * After this it behaves like a cold miss (shows a loading state). Default 5min.
   */
  gcTime?: number;
  /** Max number of pages to keep before evicting the oldest. Default 50. */
  maxEntries?: number;
}

export class PaginationCache<T> {
  private readonly store = new Map<string, CacheEntry<T>>();
  private readonly staleTime: number;
  private readonly gcTime: number;
  private readonly maxEntries: number;

  constructor(options: PaginationCacheOptions = {}) {
    this.staleTime = options.staleTime ?? 30_000;
    this.gcTime = options.gcTime ?? 300_000;
    this.maxEntries = options.maxEntries ?? 50;
  }

  /** Builds a stable key from a query object, ignoring null/undefined values. */
  static buildKey(query: Record<string, unknown>): string {
    return stableStringify(query);
  }

  get(key: string): CacheLookup<T> | undefined {
    const entry = this.store.get(key);
    if (!entry) return undefined;

    const age = Date.now() - entry.storedAt;
    if (age > this.gcTime) {
      this.store.delete(key);
      return undefined;
    }

    // Refresh recency for LRU eviction.
    this.store.delete(key);
    this.store.set(key, entry);
    return { page: entry.page, isStale: age > this.staleTime };
  }

  set(key: string, page: CachedPage<T>): void {
    this.store.delete(key);
    this.store.set(key, { page, storedAt: Date.now() });

    while (this.store.size > this.maxEntries) {
      const oldest = this.store.keys().next().value;
      if (oldest === undefined) break;
      this.store.delete(oldest);
    }
  }

  /** Clears the entire cache. Call when the underlying data mutates. */
  clear(): void {
    this.store.clear();
  }
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (value instanceof Date) return JSON.stringify(value.toISOString());

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`);
    return `{${entries.join(',')}}`;
  }

  return JSON.stringify(value);
}
